import { CacheService } from './CacheService';
import { SearchSuggestion } from '../types';
import { Post } from '../types/post';

export class RedditService {
  private cacheService: CacheService;
  private readonly SEARCH_CACHE_PREFIX = 'subreddit_search_';
  private readonly SEARCH_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly POST_CACHE_PREFIX = 'reddit_post_';
  private readonly USER_POSTS_CACHE_PREFIX = 'reddit_user_posts_';

  constructor() {
    this.cacheService = new CacheService();
  }

  async fetchPosts(subreddit: string, afterParam = ''): Promise<{ posts: Post[], nextAfter: string }> {
    const cachedData = this.cacheService.getCachedData(subreddit, afterParam);
    let data;

    if (cachedData) {
      console.log('Using cached data for', subreddit, afterParam);
      data = cachedData;
    } else {
      console.log('Fetching fresh data for', subreddit, afterParam);
      const response = await fetch(
        `https://api.reddit.com/r/${subreddit}/hot?limit=15${afterParam ? `&after=${afterParam}` : ''}`
      );
      data = await response.json();
      this.cacheService.setCachedData(subreddit, data, afterParam);
    }

    const posts = data.data.children.filter((post: Post) =>
      post.data.preview?.reddit_video_preview ||
      post.data.preview?.images?.[0] ||
      post.data.secure_media_embed?.content
    );

    return {
      posts,
      nextAfter: data.data.after
    };
  }

  private getSearchCacheKey(query: string): string {
    return `${this.SEARCH_CACHE_PREFIX}${query.toLowerCase()}`;
  }

  private getCachedSearchResults(query: string): SearchSuggestion[] | null {
    try {
      const cacheKey = this.getSearchCacheKey(query);
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();

      if (now - timestamp > this.SEARCH_CACHE_DURATION) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error reading from search cache:', error);
      return null;
    }
  }

  private setCachedSearchResults(query: string, data: SearchSuggestion[]) {
    try {
      const cacheKey = this.getSearchCacheKey(query);
      const cacheData = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error writing to search cache:', error);
      this.cleanupSearchCache();
    }
  }

  private cleanupSearchCache() {
    const now = Date.now();
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.SEARCH_CACHE_PREFIX)) {
        try {
          const cached = JSON.parse(localStorage.getItem(key) || '');
          if (now - cached.timestamp > this.SEARCH_CACHE_DURATION) {
            localStorage.removeItem(key);
          }
        } catch (e) {
          localStorage.removeItem(key);
        }
      }
    }
  }

  async searchSubreddits(query: string): Promise<SearchSuggestion[]> {
    if (query.length < 2) return [];

    // Check cache first
    const cachedResults = this.getCachedSearchResults(query);
    if (cachedResults) {
      return cachedResults;
    }

    try {
      const response = await fetch(
        `https://www.reddit.com/api/subreddit_autocomplete_v2.json?query=${encodeURIComponent(query)}&include_over_18=true&limit=10`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const suggestions = data.data.children;

      // Cache the results
      this.setCachedSearchResults(query, suggestions);

      return suggestions;
    } catch (error) {
      console.error('Error fetching subreddit suggestions:', error);
      return [];
    }
  }

  private getPostCacheKey(postId: string): string {
    return `${this.POST_CACHE_PREFIX}${postId}`;
  }

  private getUserPostsCacheKey(username: string): string {
    return `${this.USER_POSTS_CACHE_PREFIX}${username}`;
  }

  private getCachedData<T>(key: string): T | null {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp > this.CACHE_DURATION) {
        localStorage.removeItem(key);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error reading from cache:', error);
      localStorage.removeItem(key);
      return null;
    }
  }

  private setCachedData<T>(key: string, data: T): void {
    try {
      const cacheData = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error writing to cache:', error);
      this.cleanupCache(); // Try to free up space by removing old entries
    }
  }

  private cleanupCache(): void {
    const now = Date.now();
    const prefixes = [this.POST_CACHE_PREFIX, this.USER_POSTS_CACHE_PREFIX];

    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && prefixes.some(prefix => key.startsWith(prefix))) {
        try {
          const cached = JSON.parse(localStorage.getItem(key) || '');
          if (now - cached.timestamp > this.CACHE_DURATION) {
            localStorage.removeItem(key);
          }
        } catch (e) {
          localStorage.removeItem(key);
        }
      }
    }
  }

  async fetchPostById(postId: string): Promise<Post | null> {
    // Check cache first
    const cacheKey = this.getPostCacheKey(postId);
    const cachedPost = this.getCachedData<Post>(cacheKey);

    if (cachedPost) {
      console.log('Using cached post data for', postId);
      return cachedPost;
    }

    try {
      console.log('Fetching fresh post data for', postId);
      const response = await fetch(`https://api.reddit.com/api/info.json?id=t3_${postId}`);
      const data = await response.json();

      if (data?.data?.children?.[0]) {
        const post = data.data.children[0];
        // Check if post has media content
        if (post.data.preview?.reddit_video_preview ||
          post.data.preview?.images?.[0] ||
          post.data.secure_media_embed?.content) {

          // Cache the post
          this.setCachedData(cacheKey, post);
          return post;
        }
      }
      return null;
    } catch (error) {
      console.error('Error fetching post by ID:', error);
      return null;
    }
  }

  async fetchUserPosts(username: string): Promise<Post[]> {
    if (!username) return [];

    // Check cache first
    const cacheKey = this.getUserPostsCacheKey(username);
    const cachedPosts = this.getCachedData<Post[]>(cacheKey);

    if (cachedPosts) {
      console.log('Using cached user posts for', username);
      return cachedPosts;
    }

    try {
      console.log('Fetching fresh user posts for', username);
      const response = await fetch(`https://api.reddit.com/user/${username}/submitted?limit=10`);
      const data = await response.json();

      // Filter posts to only include ones with media content
      const posts = data.data.children.filter((post: Post) =>
        post.data.preview?.reddit_video_preview ||
        post.data.preview?.images?.[0] ||
        post.data.secure_media_embed?.content
      );

      // Cache the filtered posts
      this.setCachedData(cacheKey, posts);
      return posts;
    } catch (error) {
      console.error('Error fetching user posts:', error);
      return [];
    }
  }

}
